import { useContext, useMemo } from 'react';
import { faUser } from '@fortawesome/pro-regular-svg-icons';
import { ContactTypeOptions } from 'components/filter/ContactTypeOptions';
import { FilterComboboxItems } from 'components/filter/filters-next/FilterComboboxItems';
import { FilterContainer } from 'components/filter/filters-next/FilterContainer';
import { FilterContext } from 'components/filter/filters-next/FilterContext';
import { FilterForm } from 'components/filter/filters-next/FilterForm';
import { FilterFormContext } from 'components/filter/filters-next/FilterFormContext';
import { FilterSubmit } from 'components/filter/filters-next/FilterSubmit';
import { ReadableFilterValue } from 'components/filter/filters-next/ReadableFilterValue';
import { transformMultiselectToReadable } from 'components/filter/filters-next/readable';
import { useCurrentFilter } from 'components/filter/filters-next/useCurrentFilter';
import { useFilter } from 'components/filter/filters-next/useFilter';
import { injectEquals } from 'components/filter/filters-next/util';
import {
    ContentFilterComponent,
    DownstreamFilterTransform,
    FilterFormValue,
    FilterIcon,
    FilterValue,
    LabelFilterComponent,
    ReadableFilterComponent,
    ServerKey,
    UpstreamFilterTransform,
} from 'components/filter/types';
import { zeroContactTypesSelected } from 'components/filter/validations';
import * as Form from 'components/form';
import { ContactType } from 'constants/contacts';
import { PledgeType } from 'constants/pledges';
import { capitalize, identity } from 'lodash';

interface ContactTypeFilterFormValues extends FilterFormValue<ContactType[]> {}

export const filterKey: ServerKey = 'contact_type';

export const Icon: FilterIcon = faUser;

export const Filter: ContentFilterComponent = () => {
    const { defaultValue, ...rest } = useFilter<ContactType[]>();
    const { strategyType: strategyTypeRaw, relationalServerKey } = useContext(FilterContext);
    const strategyType = strategyTypeRaw as ServerKey;
    const methods = Form.useForm<ContactTypeFilterFormValues>({
        defaultValues: {
            value: defaultValue ? defaultValue.value : [],
        },
        mode: 'onChange',
    });
    const { control, path } = methods;

    const ctx = useMemo(
        () => ({
            defaultValue,
            ...rest,
        }),
        [defaultValue, rest],
    );

    return (
        <FilterFormContext.Provider value={ctx}>
            <Form.Provider {...methods}>
                <FilterForm>
                    <FilterContainer>
                        <Form.Container>
                            <Form.Label name={path('value')}>
                                <Label
                                    strategyType={strategyType}
                                    relationalServerKey={relationalServerKey}
                                />
                            </Form.Label>
                            <Form.Combobox
                                control={control}
                                name="value"
                                rules={{ validate: zeroContactTypesSelected }}
                            >
                                <FilterComboboxItems>
                                    <ContactTypeOptions />
                                </FilterComboboxItems>
                            </Form.Combobox>
                            <Form.Error name={path('value')} />
                        </Form.Container>
                    </FilterContainer>
                    <FilterSubmit />
                </FilterForm>
            </Form.Provider>
        </FilterFormContext.Provider>
    );
};

export const Readable: ReadableFilterComponent = () => {
    const currentFilter = useCurrentFilter<PledgeType[]>();
    const value = currentFilter?.value ?? [];

    return (
        <ReadableFilterValue>
            {transformMultiselectToReadable(value, {
                transform: capitalize,
            })}
        </ReadableFilterValue>
    );
};

export const Label: LabelFilterComponent = () => 'Contact type';

export const downstream: DownstreamFilterTransform<PledgeType> = identity;

export const upstream: UpstreamFilterTransform<PledgeType> = (filter: FilterValue) =>
    injectEquals({
        value: filter.value,
    });
